import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MessageSquare,
  Send,
  Bot,
  User,
  Sparkles,
  Globe,
  BookOpen,
  Code,
  Link as LinkIcon,
  Copy,
  Check,
  RefreshCw,
  Mic,
  Paperclip,
  X,
  Loader,
  Brain,
  TrendingUp,
  Clock,
  Lightbulb,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, resolveAiUserId, getOrCreateAiSessionId } from '../services/api-client';
import { api } from '@/services/client';
import { tabService } from '@/services/tabs';
import { useAuthStore } from '@/stores/authStore';
import { EmbeddedSkillRail } from '@/components/skill';
import { toast } from 'sonner';
import { useHubShellEmbedded } from '@/components/layout/HubShellContext';
import { cn } from '@/utils';

function parseMessages(raw: unknown): unknown[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function priorityToRelevance(p?: string): number {
  switch (p) {
    case 'urgent':
      return 100;
    case 'high':
      return 90;
    case 'medium':
      return 75;
    case 'low':
      return 60;
    default:
      return 70;
  }
}

function parseJsonField<T>(v: unknown): T | null {
  if (v == null) return null;
  if (typeof v === 'object') return v as T;
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as T;
    } catch {
      return null;
    }
  }
  return null;
}

function mapRowToConversation(row: Record<string, unknown>): Conversation {
  const rawList = parseMessages(row.messages);
  const messages: Message[] = rawList.map((m: unknown, i: number) => {
    const msg = m as Record<string, unknown>;
    return {
      id: String(msg.id ?? `m-${i}`),
      role: (msg.role as Message['role']) || 'user',
      content: String(msg.content ?? ''),
      timestamp: new Date((msg.created_at as string) || Date.now()),
    };
  });
  const firstUser = messages.find((m) => m.role === 'user');
  const rawTitle = firstUser?.content?.trim() || 'New conversation';
  const title = rawTitle.length > 56 ? `${rawTitle.slice(0, 56)}…` : rawTitle;
  const lastAt = (row.updated_at as string) || (row.created_at as string) || new Date().toISOString();
  const ctype = row.conversation_type ? String(row.conversation_type) : '';
  return {
    id: String(row.id),
    title,
    messages,
    createdAt: new Date((row.created_at as string) || Date.now()),
    lastActivity: new Date(lastAt),
    tags: ctype ? [ctype] : [],
  };
}

function modeToConversationType(
  mode: AIMode
): 'general' | 'tab_context' | 'task_assist' | 'discovery' | 'troubleshooting' {
  switch (mode) {
    case 'research':
      return 'discovery';
    case 'code':
      return 'task_assist';
    case 'analyze':
      return 'troubleshooting';
    default:
      return 'general';
  }
}

function hostFromUrl(url?: string): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return url.length > 48 ? `${url.slice(0, 45)}…` : url;
  }
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  context?: ContextItem[];
  suggestions?: string[];
  artifacts?: Artifact[];
  isTyping?: boolean;
}

interface ContextItem {
  type: 'tab' | 'bookmark' | 'search' | 'document';
  title: string;
  url?: string;
  relevance: number;
  excerpt?: string;
  sourceId: string;
}

interface Artifact {
  type: 'code' | 'text' | 'image' | 'link';
  content: string;
  language?: string;
  title?: string;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date;
  lastActivity: Date;
  tags: string[];
}

type AIMode = 'chat' | 'research' | 'code' | 'analyze';

function appendExchange(conv: Conversation, userText: string, assistantText: string): Conversation {
  const now = new Date();
  return {
    ...conv,
    messages: [
      ...conv.messages,
      { id: `u-${now.getTime()}`, role: 'user', content: userText, timestamp: now },
      { id: `a-${now.getTime() + 1}`, role: 'assistant', content: assistantText, timestamp: new Date(now.getTime() + 1) },
    ],
    lastActivity: now,
  };
}

export function ConversationalAI() {
  const embeddedInHub = useHubShellEmbedded();
  const user = useAuthStore((state) => state.user);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [processingStage, setProcessingStage] = useState<'idle' | 'fetching_context' | 'sending' | 'waiting_response'>('idle');
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [aiMode, setAiMode] = useState<AIMode>('chat');
  const [showContextPanel, setShowContextPanel] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [selectedContext, setSelectedContext] = useState<ContextItem[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [suggestionItems, setSuggestionItems] = useState<ContextItem[]>([]);
  const [insightItems, setInsightItems] = useState<ContextItem[]>([]);
  const [predictionItems, setPredictionItems] = useState<ContextItem[]>([]);
  const [tabItems, setTabItems] = useState<ContextItem[]>([]);
  const [workspaceSessionId, setWorkspaceSessionId] = useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [tabCount, setTabCount] = useState(0);
  const [showConvDropdown, setShowConvDropdown] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const resolveUserId = useCallback(() => resolveAiUserId(user?.id), [user?.id]);

  const resolveWorkspaceSession = useCallback(async () => {
    setSessionLoading(true);
    try {
      const cached = sessionStorage.getItem('khiimori_workspace_session_id');
      if (cached) {
        setWorkspaceSessionId(cached);
        const { tabs, total } = await tabService.getTabsBySession(cached, 1, 200);
        setTabCount(total);
        setTabItems(tabs.map((t, idx) => ({
          type: 'tab' as const,
          sourceId: t.id || `tab-${idx}`,
          title: t.title || '(Untitled Tab)',
          url: t.url,
          excerpt: t.url,
          relevance: 90,
        })));
        setSessionLoading(false);
        return;
      }
      const cur = await api.get<{ session: { id: string } }>('/sessions/current');
      const id = cur.data.session.id;
      sessionStorage.setItem('khiimori_workspace_session_id', id);
      setWorkspaceSessionId(id);
      const { tabs, total } = await tabService.getTabsBySession(id, 1, 200);
      setTabCount(total);
      setTabItems(tabs.map((t, idx) => ({
        type: 'tab' as const,
        sourceId: t.id || `tab-${idx}`,
        title: t.title || '(Untitled Tab)',
        url: t.url,
        excerpt: t.url,
        relevance: 90,
      })));
    } catch {
      try {
        const list = await api.get<{ sessions: { id: string }[] }>('/sessions');
        const first = list.data.sessions?.[0]?.id;
        if (first) {
          sessionStorage.setItem('khiimori_workspace_session_id', first);
          setWorkspaceSessionId(first);
          const { tabs, total } = await tabService.getTabsBySession(first, 1, 200);
          setTabCount(total);
        } else {
          const created = await api.post<{ id: string }>('/sessions', { name: 'AI Workspace' });
          const id = (created.data as { id?: string }).id;
          if (id) {
            sessionStorage.setItem('khiimori_workspace_session_id', id);
            setWorkspaceSessionId(id);
            const { tabs, total } = await tabService.getTabsBySession(id, 1, 200);
            setTabCount(total);
          }
        }
      } catch {
        setWorkspaceSessionId(null);
      }
    } finally {
      setSessionLoading(false);
    }
  }, []);

  const refreshConversations = useCallback(async () => {
    const uid = resolveUserId();
    setIsLoadingList(true);
    setListError(null);
    try {
      const rows = await apiClient.getAiConversationHistory(uid, 30);
      const mapped = (rows || []).map((r) => mapRowToConversation(r as Record<string, unknown>));
      mapped.sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());
      setConversations(mapped);
      setActiveConversation((prev) => {
        if (mapped.length === 0) return null;
        if (prev && mapped.some((c) => c.id === prev.id)) {
          return mapped.find((c) => c.id === prev.id)!;
        }
        return mapped[0];
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load conversations';
      setListError(msg);
      // Keep existing conversations visible if refresh fails.
    } finally {
      setIsLoadingList(false);
    }
  }, [resolveUserId]);

  const loadContextSources = useCallback(async () => {
    const uid = resolveUserId();
    setContextLoading(true);
    setSuggestionsError(null);
    setDashboardError(null);
    try {
      const [sugErr, dashErr] = await Promise.all([
        apiClient.getAiSuggestions(uid)
          .then((sug) => {
            const suggestions: ContextItem[] = (sug || []).map((s: Record<string, unknown>, i: number) => {
              const sc =
                parseJsonField<{
                  title?: string;
                  description?: string;
                  priority?: string;
                }>(s.suggestion_content) || {};
              const pr = sc.priority;
              return {
                type: 'document' as const,
                sourceId: String(s.id ?? `sug-${i}`),
                title: sc.title || String(s.suggestion_type || 'Suggestion'),
                excerpt: sc.description || '',
                relevance: priorityToRelevance(pr),
              };
            });
            setSuggestionItems(suggestions);
            return null;
          })
          .catch((e: unknown) => {
            const msg = e instanceof Error ? e.message : 'Failed to load suggestions';
            setSuggestionsError(msg);
            setSuggestionItems([]);
            return msg;
          }),
        apiClient.getTabMemoryDashboard(uid)
          .then((dash) => {
            const insights: ContextItem[] = [];
            const predictions: ContextItem[] = [];

            if (dash && typeof dash === 'object') {
              const d = dash as Record<string, unknown>;
              const ins = Array.isArray(d.insights) ? d.insights : [];
              ins.forEach((it: unknown, i: number) => {
                const x = it as { title?: string; description?: string };
                insights.push({
                  type: 'document',
                  sourceId: `insight-${i}-${x.title || ''}`,
                  title: x.title || 'Insight',
                  excerpt: x.description || '',
                  relevance: 78,
                });
              });
              const pats = Array.isArray(d.patterns) ? d.patterns : [];
              pats.slice(0, 8).forEach((p: unknown, i: number) => {
                const x = p as { id?: string; pattern_type?: string; confidence_score?: number };
                insights.push({
                  type: 'document',
                  sourceId: String(x.id ?? `pattern-${i}`),
                  title: String(x.pattern_type || 'pattern').replace(/_/g, ' '),
                  excerpt: '',
                  relevance: Math.round((Number(x.confidence_score) || 0.5) * 100),
                });
              });
              const preds = Array.isArray(d.predictions) ? d.predictions : [];
              preds.forEach((p: unknown, i: number) => {
                const x = p as {
                  id?: string;
                  predicted_action?: string;
                  target_tab_title?: string;
                  target_tab_url?: string;
                  confidence?: number;
                  reasoning?: string;
                };
                predictions.push({
                  type: 'tab',
                  sourceId: String(x.id ?? `pred-${i}`),
                  title: x.target_tab_title || x.predicted_action || 'Prediction',
                  url: x.target_tab_url,
                  excerpt: x.reasoning || '',
                  relevance: Math.round((Number(x.confidence) || 0.5) * 100),
                });
              });
            }

            setInsightItems(insights);
            setPredictionItems(predictions);
            return null;
          })
          .catch((e: unknown) => {
            const msg = e instanceof Error ? e.message : 'Failed to load dashboard';
            setDashboardError(msg);
            setInsightItems([]);
            setPredictionItems([]);
            return msg;
          }),
      ]);
    } finally {
      setContextLoading(false);
    }
  }, [resolveUserId]);

  useEffect(() => {
    void resolveWorkspaceSession();
  }, [resolveWorkspaceSession]);

  useEffect(() => {
    if (!sessionLoading) {
      refreshConversations();
    }
  }, [refreshConversations, sessionLoading]);

  useEffect(() => {
    loadContextSources();
  }, [loadContextSources]);

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages, isSending]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleNewConversation = async () => {
    const uid = resolveUserId();
    setIsCreating(true);
    setListError(null);
    try {
      const row = await apiClient.createAiConversation({
        userId: uid,
        sessionId: workspaceSessionId || getOrCreateAiSessionId(),
        type: modeToConversationType(aiMode),
      });
      const conv = mapRowToConversation(row as Record<string, unknown>);
      setConversations((prev) => [conv, ...prev]);
      setActiveConversation(conv);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to create conversation';
      setListError(msg);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    const text = inputText.trim();
    setSendError(null);
    setIsSending(true);

    let conversation = activeConversation;
    if (!conversation) {
      const uid = resolveUserId();
      try {
        const row = await apiClient.createAiConversation({
          userId: uid,
          sessionId: workspaceSessionId || getOrCreateAiSessionId(),
          type: modeToConversationType(aiMode),
        });
        conversation = mapRowToConversation(row as Record<string, unknown>);
        setConversations((prev) => [conversation!, ...prev]);
        setActiveConversation(conversation);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to create conversation';
        setSendError(msg);
        setIsSending(false);
        return;
      }
    }

    const ctxBlock =
      selectedContext.length > 0
        ? `Context:\n${selectedContext.map((c) => `- ${c.title}`).join('\n')}\n\n`
        : '';
    const fullMessage = `${ctxBlock}${text}`;

    setProcessingStage('fetching_context');
    setInputText('');

    try {
      setProcessingStage('sending');
      const result = await apiClient.respondAiConversation(conversation.id, fullMessage, aiMode);
      setProcessingStage('waiting_response');
      setSelectedContext([]);

      const assistantText = result.response || 'No response received.';
      const updated = appendExchange(conversation, text, assistantText);
      setActiveConversation(updated);
      setConversations((prev) => {
        const exists = prev.some((c) => c.id === updated.id);
        if (!exists) return [updated, ...prev];
        return prev.map((c) => (c.id === updated.id ? updated : c));
      });
      void refreshConversations();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to send message';
      setSendError(msg);
      toast.error(msg);
      setInputText(text);
      setProcessingStage('idle');
    } finally {
      setIsSending(false);
      setProcessingStage('idle');
    }
  };

  const handleCopyMessage = (messageId: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const isContextSelected = (item: ContextItem) =>
    selectedContext.some((c) => c.sourceId === item.sourceId);

  const toggleContext = (item: ContextItem) => {
    setSelectedContext((prev) =>
      prev.some((c) => c.sourceId === item.sourceId)
        ? prev.filter((c) => c.sourceId !== item.sourceId)
        : [...prev, item]
    );
  };

  const modeButtons: { id: AIMode; label: string; icon: React.ReactNode }[] = [
    { id: 'chat', label: 'Chat', icon: <MessageSquare className="w-3.5 h-3.5" /> },
    { id: 'research', label: 'Research', icon: <BookOpen className="w-3.5 h-3.5" /> },
    { id: 'code', label: 'Code', icon: <Code className="w-3.5 h-3.5" /> },
    { id: 'analyze', label: 'Analyze', icon: <TrendingUp className="w-3.5 h-3.5" /> },
  ];

  return (
    <div
      className={cn(
        'ai-chat-shell min-w-0 max-w-full overflow-hidden flex',
        embeddedInHub
          ? '-mx-6 h-[calc(100dvh-10.5rem)] min-h-[32rem] rounded-xl border border-khiimori-border bg-khiimori-bg-secondary/40 lg:-mx-8'
          : 'h-screen bg-khiimori-bg-primary text-white',
      )}
    >
      {/* Conversation sidebar */}
      <div className="hidden w-60 shrink-0 flex-col border-r border-khiimori-border bg-khiimori-bg-elevated/30 md:flex lg:w-64">
        <div className="border-b border-khiimori-border p-3">
          <Button
            onClick={handleNewConversation}
            disabled={isCreating}
            isLoading={isCreating}
            variant="gradient"
            leftIcon={<MessageSquare className="w-4 h-4" />}
            className="w-full"
            size="sm"
          >
            New chat
          </Button>
          {!sessionLoading && (
            <p className="mt-2 text-center text-[11px] text-khiimori-text-muted">
              {tabCount > 0 ? `${tabCount} tabs in workspace` : 'No tabs synced yet'}
            </p>
          )}
        </div>

        <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
          {isLoadingList && (
            <div className="flex justify-center py-8 text-khiimori-text-muted">
              <Loader className="w-6 h-6 animate-spin" />
            </div>
          )}
          {!isLoadingList && listError && (
            <div className="rounded-lg border border-error-500/30 bg-error-500/10 p-2 text-xs text-error-300">{listError}</div>
          )}
          {!isLoadingList && !listError && conversations.length === 0 && (
            <p className="px-2 py-6 text-center text-xs text-khiimori-text-muted">
              No chats yet — type below or click New chat.
            </p>
          )}
          {!isLoadingList &&
            conversations.map((conv) => (
              <motion.button
                key={conv.id}
                layout
                onClick={() => setActiveConversation(conv)}
                className={cn(
                  'relative w-full rounded-lg border p-2.5 text-left transition-all',
                  activeConversation?.id === conv.id
                    ? 'border-primary-500/50 bg-primary-500/15'
                    : 'border-transparent bg-khiimori-bg-elevated/20 hover:bg-khiimori-bg-elevated/40',
                )}
              >
                {activeConversation?.id === conv.id && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute bottom-2 left-0 top-2 w-0.5 rounded-full bg-primary-400"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
                <div className="truncate text-sm font-medium text-khiimori-text-primary">{conv.title}</div>
                <div className="mt-0.5 flex items-center gap-1 text-[10px] text-khiimori-text-muted">
                  <Clock className="h-3 w-3" />
                  {conv.lastActivity.toLocaleDateString()}
                </div>
              </motion.button>
            ))}
        </div>

        <div className="border-t border-khiimori-border p-2">
          <Button
            type="button"
            onClick={() => refreshConversations()}
            disabled={isLoadingList}
            isLoading={isLoadingList}
            variant="glass"
            leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
            size="sm"
            className="w-full"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Main chat column */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Compact toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-khiimori-border bg-khiimori-bg-elevated/20 px-3 py-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-primary">
            <Brain className="h-4 w-4" />
          </div>

          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {modeButtons.map((mode) => (
              <button
                key={mode.id}
                type="button"
                onClick={() => setAiMode(mode.id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all',
                  aiMode === mode.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/40 hover:text-khiimori-text-primary',
                )}
              >
                {mode.icon}
                {mode.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handleNewConversation}
              disabled={isCreating}
              className="rounded-lg border border-khiimori-border bg-khiimori-bg-elevated/30 px-2.5 py-1.5 text-xs text-khiimori-text-secondary hover:text-khiimori-text-primary md:hidden"
            >
              + New
            </button>
            <button
              type="button"
              onClick={() => setShowContextPanel((v) => !v)}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs transition-all',
                showContextPanel
                  ? 'bg-primary-500/20 text-primary-300'
                  : 'text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/40',
              )}
            >
              <Globe className="h-3.5 w-3.5" />
              Context
            </button>
          </div>
        </div>

        {/* Quick suggestions — only when context panel is closed */}
        {!showContextPanel && suggestionItems.length > 0 && (
          <div className="border-b border-khiimori-border bg-primary-500/5 px-3 py-2">
            <div className="mb-1.5 flex items-center gap-1.5">
              <Lightbulb className="h-3.5 w-3.5 text-amber-400" />
              <span className="text-[11px] font-medium text-khiimori-text-secondary">Try asking</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {suggestionItems
                .filter((s) => !dismissedSuggestions.has(s.sourceId))
                .slice(0, 4)
                .map((item) => (
                  <button
                    key={item.sourceId}
                    type="button"
                    onClick={() => setInputText(item.title)}
                    className="rounded-full border border-primary-500/25 bg-primary-500/10 px-2.5 py-1 text-[11px] text-khiimori-text-secondary transition-all hover:bg-primary-500/20 hover:text-khiimori-text-primary"
                  >
                    {item.title}
                  </button>
                ))}
            </div>
          </div>
        )}

        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-khiimori-bg-primary/40">
          <div className={cn('min-h-0 flex-1 overflow-y-auto p-4 lg:p-6', showContextPanel ? '' : 'mx-auto max-w-4xl w-full')}>
            {sendError && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 text-sm text-error-300 bg-error-500/10 border border-error-500/30 rounded-lg p-3"
              >
                {sendError}
              </motion.div>
            )}
            {!activeConversation ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-2xl border border-dashed border-khiimori-border/60 bg-khiimori-bg-elevated/15 px-6 py-10 text-center"
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-primary">
                  <Sparkles className="h-7 w-7 text-white" />
                </div>
                <h2 className="text-lg font-semibold text-khiimori-text-primary">Ask anything about your tabs</h2>
                <p className="mt-2 max-w-md text-sm text-khiimori-text-muted">
                  Type a message below to start a new chat, or pick a suggestion. Your workspace context can be attached from the Context panel.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {['Summarize my tabs', 'Close duplicate tabs', 'Stash research tabs', 'What should I focus on?'].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setInputText(suggestion)}
                      className="rounded-lg border border-khiimori-border/60 bg-khiimori-bg-elevated/30 px-3 py-1.5 text-xs text-khiimori-text-secondary transition-colors hover:border-primary-500/40 hover:text-khiimori-text-primary"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
                <Button
                  type="button"
                  onClick={handleNewConversation}
                  disabled={isCreating}
                  isLoading={isCreating}
                  variant="gradient"
                  className="mt-5"
                  leftIcon={<MessageSquare className="w-4 h-4" />}
                >
                  Start new chat
                </Button>
              </motion.div>
            ) : activeConversation.messages.length > 0 ? (
              <div className="space-y-6">
                  {activeConversation.messages.map((message, idx) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, ease: 'easeOut', delay: Math.min(idx * 0.04, 0.2) }}
                    >
                      <MessageBubble
                        message={message}
                        onCopy={handleCopyMessage}
                        copiedId={copiedMessageId}
                      />
                    </motion.div>
                  ))}
                {isSending && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-start gap-3"
                  >
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary-500/30 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
                      <div className="relative w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0 ring-2 ring-primary-400/40">
                        <Bot className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="bg-khiimori-bg-elevated/5 rounded-2xl rounded-tl-none px-4 py-3 border border-khiimori-border/50">
                      <div className="flex flex-col gap-2 min-w-[160px]">
                        <div className="flex gap-1.5 items-end h-5">
                          <span
                            className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-bounce"
                            style={{ animationDelay: '0ms', animationDuration: '0.6s' }}
                          />
                          <span
                            className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-bounce"
                            style={{ animationDelay: '0.15s', animationDuration: '0.6s' }}
                          />
                          <span
                            className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-bounce"
                            style={{ animationDelay: '0.3s', animationDuration: '0.6s' }}
                          />
                        </div>
                        <span className="text-xs text-khiimori-text-muted animate-pulse-subtle">
                          {processingStage === 'fetching_context' && 'Fetching workspace context...'}
                          {processingStage === 'sending' && 'Sending to AI...'}
                          {processingStage === 'waiting_response' && 'AI is thinking...'}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="h-full flex items-center justify-center"
              >
                <div className="text-center max-w-lg mx-auto px-4">
                  <motion.div
                    animate={{ scale: [1, 1.08, 1], rotate: [0, 3, 0, -3, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-6"
                  >
                    <Sparkles className="w-8 h-8" />
                  </motion.div>
                  <h2 className="text-xl font-bold mb-2">Start a Conversation</h2>
                  <p className="text-khiimori-text-muted mb-6 text-sm">
                    Messages are saved to your workspace. Add context from the panel on the right when available.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {['Summarize my tabs', 'Help me organize', 'Search my history', 'Start collaboration'].map((suggestion) => (
                      <motion.button
                        key={suggestion}
                        type="button"
                        onClick={() => setInputText(suggestion)}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="px-4 py-2 bg-khiimori-bg-elevated/5 hover:bg-khiimori-bg-elevated/10 rounded-lg transition-all text-sm"
                      >
                        {suggestion}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          <AnimatePresence>
            {showContextPanel && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 300, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                className="h-full shrink-0 overflow-hidden border-l border-khiimori-border bg-khiimori-bg-elevated/25"
              >
                <ContextPanel
                  selectedContext={selectedContext}
                  onToggleContext={toggleContext}
                  isContextSelected={isContextSelected}
                  tabs={tabItems}
                  suggestions={suggestionItems}
                  insights={insightItems}
                  predictions={predictionItems}
                  loading={contextLoading}
                  suggestionsError={suggestionsError}
                  dashboardError={dashboardError}
                  onRefreshContext={loadContextSources}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="border-t border-khiimori-border bg-khiimori-bg-elevated/25 p-3 lg:px-4">
          <div className="mx-auto w-full max-w-4xl">
            {selectedContext.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {selectedContext.map((item) => (
                  <div
                    key={item.sourceId}
                    className="px-3 py-1.5 bg-primary-500/20 border border-primary-500/50 rounded-lg flex items-center gap-2 text-sm"
                  >
                    <Globe className="w-3 h-3" />
                    <span className="line-clamp-1">{item.title}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedContext((prev) => prev.filter((c) => c.sourceId !== item.sourceId))}
                      className="hover:bg-khiimori-bg-elevated/10 rounded p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={activeConversation ? `Ask anything… (${aiMode} mode)` : 'Type to start a new conversation…'}
                  disabled={isSending}
                  className="w-full px-4 py-3 pr-20 bg-khiimori-bg-elevated/5 border border-khiimori-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none max-h-32 disabled:opacity-50"
                  rows={1}
                />
                <div className="absolute right-2 bottom-2 flex items-center gap-1">
                  <button type="button" className="p-2 hover:bg-khiimori-bg-elevated/10 rounded-lg transition-all">
                    <Paperclip className="w-4 h-4 text-khiimori-text-muted" />
                  </button>
                  <button type="button" className="p-2 hover:bg-khiimori-bg-elevated/10 rounded-lg transition-all">
                    <Mic className="w-4 h-4 text-khiimori-text-muted" />
                  </button>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isSending}
                variant="gradient"
                size="md"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>

            <div className="mt-1.5 text-center text-[10px] text-khiimori-text-muted">
              {selectedContext.length} context item{selectedContext.length !== 1 ? 's' : ''} selected · Enter to send
            </div>
          </div>
        </div>

        <EmbeddedSkillRail
          cardClassName="mx-3 mb-3 mt-0 border-0 border-t border-khiimori-border bg-transparent shadow-none pt-3"
          padding="none"
          title="AI skills"
          restrictCategories={['analyze', 'predict']}
          gridClassName="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        />
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  onCopy,
  copiedId,
}: {
  message: Message;
  onCopy: (id: string, content: string) => void;
  copiedId: string | null;
}) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser ? 'bg-gradient-to-br from-purple-600 to-pink-600' : 'bg-gradient-primary'
        }`}
      >
        {isUser ? <User className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
      </div>

      <div className={`flex-1 max-w-3xl ${isUser ? 'flex flex-col items-end' : ''}`}>
        <div
          className={`rounded-2xl p-4 ${
            isUser ? 'bg-gradient-to-br from-purple-600 to-pink-600 rounded-tr-none' : 'bg-khiimori-bg-elevated/5 rounded-tl-none'
          }`}
        >
          {isUser ? (
            <p className="text-khiimori-text-primary whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="markdown-body text-sm text-khiimori-text-secondary [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h1 className="text-xl font-bold text-khiimori-text-primary mt-4 mb-2">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-lg font-bold text-khiimori-text-primary mt-4 mb-2">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-base font-semibold text-khiimori-text-primary mt-3 mb-1.5">{children}</h3>,
                  h4: ({ children }) => <h4 className="text-sm font-semibold text-khiimori-text-primary mt-3 mb-1">{children}</h4>,
                  h5: ({ children }) => <h5 className="text-sm font-medium text-khiimori-text-primary mt-2 mb-1">{children}</h5>,
                  h6: ({ children }) => <h6 className="text-xs font-medium text-khiimori-text-secondary mt-2 mb-1">{children}</h6>,
                  p: ({ children }) => <p className="mb-2 text-khiimori-text-secondary">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-2 pl-2 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 pl-2 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="text-khiimori-text-secondary">{children}</li>,
                  code: ({ className, children, ...props }) => {
                    const isInline = !className;
                    if (isInline) {
                      return <code className="bg-khiimori-bg-elevated/10 text-primary-300 rounded px-1.5 py-0.5 text-xs font-mono" {...props}>{children}</code>;
                    }
                    return (
                      <pre className="bg-khiimori-bg-secondary/40 rounded-lg p-4 overflow-x-auto border border-khiimori-border my-3 text-sm">
                        <code className={className} {...props}>{children}</code>
                      </pre>
                    );
                  },
                  pre: ({ children }) => <>{children}</>,
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary-400 underline hover:text-primary-300 transition-colors">
                      {children}
                    </a>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-primary-500/50 pl-3 my-2 text-khiimori-text-muted italic">
                      {children}
                    </blockquote>
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-3">
                      <table className="min-w-full border border-khiimori-border rounded-lg">{children}</table>
                    </div>
                  ),
                  thead: ({ children }) => <thead className="bg-khiimori-bg-elevated/5">{children}</thead>,
                  th: ({ children }) => <th className="px-3 py-2 text-left text-xs font-medium text-khiimori-text-secondary border-b border-khiimori-border">{children}</th>,
                  td: ({ children }) => <td className="px-3 py-2 text-xs text-khiimori-text-secondary border-b border-khiimori-border/50">{children}</td>,
                  hr: () => <hr className="border-khiimori-border my-3" />,
                  strong: ({ children }) => <strong className="font-bold text-khiimori-text-primary">{children}</strong>,
                  em: ({ children }) => <em className="italic text-khiimori-text-primary">{children}</em>,
                  del: ({ children }) => <del className="line-through text-khiimori-text-muted">{children}</del>,
                  img: ({ src, alt }) => <img src={src} alt={alt} className="max-w-full rounded-lg my-2" />,
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {message.artifacts && message.artifacts.length > 0 && (
            <div className="mt-4 space-y-3">
              {message.artifacts.map((artifact: Artifact, idx: number) => (
                <ArtifactDisplay key={idx} artifact={artifact} />
              ))}
            </div>
          )}
        </div>

        {message.context && message.context.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.context.map((ctx: ContextItem, idx: number) => (
              <div
                key={idx}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-khiimori-bg-elevated/5 rounded-lg text-xs mr-2"
              >
                <Globe className="w-3 h-3 text-primary-400" />
                <span className="text-khiimori-text-secondary">{ctx.title}</span>
                <span className="text-khiimori-text-muted">{ctx.relevance}%</span>
              </div>
            ))}
          </div>
        )}

        {message.suggestions && message.suggestions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.suggestions.map((suggestion: string, idx: number) => (
              <button
                key={idx}
                type="button"
                className="px-3 py-1.5 bg-primary-500/20 hover:bg-primary-500/30 border border-primary-500/50 rounded-lg text-sm transition-all"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-center gap-3 text-xs text-khiimori-text-muted">
          <span>{message.timestamp.toLocaleTimeString()}</span>
          <button
            type="button"
            onClick={() => onCopy(message.id, message.content)}
            className="hover:text-khiimori-text-primary transition-colors flex items-center gap-1"
          >
            {copiedId === message.id ? (
              <>
                <Check className="w-3 h-3" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ArtifactDisplay({ artifact }: { artifact: Artifact }) {
  if (artifact.type === 'code') {
    return (
      <div className="bg-khiimori-bg-secondary/40 rounded-lg overflow-hidden border border-khiimori-border">
        <div className="px-4 py-2 bg-khiimori-bg-elevated/5 border-b border-khiimori-border flex items-center justify-between">
          <span className="text-xs font-medium">{artifact.title || artifact.language}</span>
          <button type="button" className="p-1 hover:bg-khiimori-bg-elevated/10 rounded transition-all">
            <Copy className="w-3 h-3" />
          </button>
        </div>
        <pre className="p-4 overflow-x-auto text-sm">
          <code>{artifact.content}</code>
        </pre>
      </div>
    );
  }

  if (artifact.type === 'text') {
    return (
      <div className="bg-khiimori-bg-elevated/5 rounded-lg p-4 border border-khiimori-border">
        <h4 className="text-sm font-semibold mb-2">{artifact.title}</h4>
        <p className="text-sm text-khiimori-text-secondary">{artifact.content}</p>
      </div>
    );
  }

  return null;
}

type ContextTab = 'tabs' | 'suggestions' | 'insights' | 'predictions';

function ContextPanel({
  selectedContext,
  onToggleContext,
  isContextSelected,
  tabs,
  suggestions,
  insights,
  predictions,
  loading,
  suggestionsError,
  dashboardError,
  onRefreshContext,
}: {
  selectedContext: ContextItem[];
  onToggleContext: (item: ContextItem) => void;
  isContextSelected: (item: ContextItem) => boolean;
  tabs: ContextItem[];
  suggestions: ContextItem[];
  insights: ContextItem[];
  predictions: ContextItem[];
  loading: boolean;
  suggestionsError: string | null;
  dashboardError: string | null;
  onRefreshContext: () => void;
}) {
  const [activeTab, setActiveTab] = useState<ContextTab>('tabs');

  const filteredItems =
    activeTab === 'tabs' ? tabs : activeTab === 'suggestions' ? suggestions : activeTab === 'insights' ? insights : predictions;

  const activeError = activeTab === 'suggestions' ? suggestionsError : activeTab === 'tabs' ? null : dashboardError;

  const emptyLabel =
    activeTab === 'tabs'
      ? 'No active tabs found in this workspace session. Open or sync tabs with the extension.'
      : activeTab === 'suggestions'
      ? 'No proactive suggestions yet. They appear when the assistant detects patterns in your workspace.'
      : activeTab === 'insights'
        ? 'No tab memory insights yet. Browse tabs and the AI will learn from your patterns.'
        : 'No predictions yet. Tab predictions are generated from your browsing patterns.';

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-khiimori-border">
        <h3 className="font-semibold mb-3 flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm">
            <Globe className="w-4 h-4 text-primary-400" />
            Context sources
          </span>
          {activeTab === 'tabs' && tabs.length > 0 && (
            <span className="text-[11px] text-primary-300 font-normal">
              {tabs.length} tabs
            </span>
          )}
        </h3>

        <div className="flex items-center gap-1 bg-khiimori-bg-elevated/5 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setActiveTab('tabs')}
            className={`flex-1 px-2.5 py-1.5 rounded text-xs transition-all ${
              activeTab === 'tabs' ? 'bg-primary-500 text-white font-medium shadow-sm' : 'hover:bg-khiimori-bg-elevated/10 text-khiimori-text-secondary'
            }`}
          >
            Tabs
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('suggestions')}
            className={`flex-1 px-2.5 py-1.5 rounded text-xs transition-all ${
              activeTab === 'suggestions' ? 'bg-primary-500 text-white font-medium shadow-sm' : 'hover:bg-khiimori-bg-elevated/10 text-khiimori-text-secondary'
            }`}
          >
            Ideas
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('insights')}
            className={`flex-1 px-2.5 py-1.5 rounded text-xs transition-all ${
              activeTab === 'insights' ? 'bg-primary-500 text-white font-medium shadow-sm' : 'hover:bg-khiimori-bg-elevated/10 text-khiimori-text-secondary'
            }`}
          >
            Insights
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('predictions')}
            className={`flex-1 px-2.5 py-1.5 rounded text-xs transition-all ${
              activeTab === 'predictions' ? 'bg-primary-500 text-white font-medium shadow-sm' : 'hover:bg-khiimori-bg-elevated/10 text-khiimori-text-secondary'
            }`}
          >
            Predict
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && (
          <div className="flex justify-center py-6 text-khiimori-text-muted">
            <Loader className="w-6 h-6 animate-spin" />
          </div>
        )}
        {!loading && activeError && (
          <div className="mb-3 p-3 bg-error-500/10 border border-error-500/30 rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs text-error-300 flex-1">{activeError}</p>
              <button
                type="button"
                onClick={onRefreshContext}
                className="ml-2 p-1 hover:bg-khiimori-bg-elevated/10 rounded transition-all"
                title="Retry"
              >
                <RefreshCw className="w-3.5 h-3.5 text-error-300" />
              </button>
            </div>
          </div>
        )}
        {!loading && !activeError && filteredItems.length === 0 && <p className="text-xs text-khiimori-text-muted text-center py-4">{emptyLabel}</p>}
        {!loading && !activeError &&
          filteredItems.map((item) => (
            <div
              key={item.sourceId}
              onClick={() => onToggleContext(item)}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                isContextSelected(item)
                  ? 'bg-primary-500/20 border-primary-500/50'
                  : 'bg-khiimori-bg-elevated/5 border-khiimori-border hover:border-khiimori-border'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-medium text-khiimori-text-primary flex-1 line-clamp-2">{item.title}</h4>
                <div className="ml-2 px-2 py-0.5 bg-primary-500/30 rounded text-xs font-medium">{item.relevance}%</div>
              </div>
              {item.excerpt ? <p className="text-xs text-khiimori-text-muted line-clamp-3">{item.excerpt}</p> : null}
              {item.url ? (
                <div className="mt-2 flex items-center gap-1 text-xs text-primary-400">
                  <LinkIcon className="w-3 h-3" />
                  <span className="truncate">{hostFromUrl(item.url)}</span>
                </div>
              ) : null}
            </div>
          ))}
      </div>

      <div className="p-4 border-t border-khiimori-border">
        <div className="text-xs text-khiimori-text-muted text-center">
          {selectedContext.length} source{selectedContext.length !== 1 ? 's' : ''} selected
        </div>
      </div>
    </div>
  );
}
